import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { Button, ConfigProvider, theme as antTheme } from "antd";
import enUS from "antd/locale/en_US";
import zhCN from "antd/locale/zh_CN";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { api } from "@/core/api/client";
import { LocaleProvider, translate } from "@/core/i18n";
import { queryClient } from "@/core/query/client";
import { resolveTheme, usePreferences } from "@/core/state/preferences";
import { useSession } from "@/core/state/session";
import { AuthGate } from "@/features/auth/AuthGate";
import { RoleGate } from "@/features/auth/RoleGate";
import { BootScreen } from "@/shared/boot/BootScreen";
import { PageErrorBoundary } from "@/shared/ui/PageErrorBoundary";
import { AppShell } from "@/shared/layout/AppShell";

const AccountPage = lazy(() => import("@/features/account/AccountPage").then((module) => ({ default: module.AccountPage })));
const AdminPage = lazy(() => import("@/features/admin/AdminPage").then((module) => ({ default: module.AdminPage })));
const LoginPage = lazy(() => import("@/features/auth/LoginPage").then((module) => ({ default: module.LoginPage })));
const DevicePage = lazy(() => import("@/features/auth/DevicePage").then((module) => ({ default: module.DevicePage })));
const InteractPage = lazy(() => import("@/features/auth/InteractPage").then((module) => ({ default: module.InteractPage })));
const AcceptInvitePage = lazy(() => import("@/features/auth/AccountRecoveryPages").then((module) => ({ default: module.AcceptInvitePage })));
const ResetPasswordPage = lazy(() => import("@/features/auth/AccountRecoveryPages").then((module) => ({ default: module.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import("@/features/auth/AccountRecoveryPages").then((module) => ({ default: module.VerifyEmailPage })));
const ApplicationsPage = lazy(() => import("@/features/applications/ApplicationsPage").then((module) => ({ default: module.ApplicationsPage })));
const DocsPage = lazy(() => import("@/features/docs/DocsPage").then((module) => ({ default: module.DocsPage })));
const DevelopApplyPage = lazy(() => import("@/features/developer/DevelopApplyPage").then((module) => ({ default: module.DevelopApplyPage })));
const DeveloperPage = lazy(() => import("@/features/developer/DeveloperPage").then((module) => ({ default: module.DeveloperPage })));
const WithdrawalPage = lazy(() => import("@/features/payment/WithdrawalPage").then((module) => ({ default: module.WithdrawalPage })));
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const NotFoundPage = lazy(() => import("@/features/errors/NotFoundPage").then((module) => ({ default: module.NotFoundPage })));
const PlatformPage = lazy(() => import("@/features/platform/PlatformPage").then((module) => ({ default: module.PlatformPage })));
const SecurityPage = lazy(() => import("@/features/security/SecurityPage").then((module) => ({ default: module.SecurityPage })));
const SetupPage = lazy(() => import("@/features/setup/SetupPage").then((module) => ({ default: module.SetupPage })));
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const SupportPage = lazy(() => import("@/features/support/SupportPage").then((module) => ({ default: module.SupportPage })));
const AnnouncementsPage = lazy(() => import("@/features/announcements/AnnouncementsPage").then((module) => ({ default: module.AnnouncementsPage })));
const LegalPage = lazy(() => import("@/features/legal/LegalPage").then((module) => ({ default: module.LegalPage })));
const PausedApplicationPage = lazy(() => import("@/features/auth/PausedApplicationPage").then((module) => ({ default: module.PausedApplicationPage })));

export function App() {
  const [setupRequired, setSetupRequired] = useState<boolean | null>(null);
  const [setupStatusError, setSetupStatusError] = useState(false);
  const [setupAttempt, setSetupAttempt] = useState(0);
  const setReady = useSession((state) => state.setReady);
  const locale = usePreferences((state) => state.locale);
  const themePreference = usePreferences((state) => state.theme);
  const compactMode = usePreferences((state) => state.compactMode);
  const reducedMotion = usePreferences((state) => state.reducedMotion);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  const resolvedTheme = themePreference === "system" ? (systemDark ? "dark" : "light") : resolveTheme(themePreference);

  useEffect(() => {
    let cancelled = false;
    setSetupStatusError(false);
    api.setupStatus().then(({ setupRequired: required }) => { if (!cancelled) { setSetupRequired(required); setReady(true); } }).catch(() => { if (!cancelled) { setSetupRequired(null); setSetupStatusError(true); setReady(true); } });
    return () => { cancelled = true; };
  }, [setReady, setupAttempt]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.dataset.compact = compactMode ? "true" : "false";
  }, [locale, resolvedTheme, compactMode]);

  const isDark = resolvedTheme === "dark";
  const antdTheme = useMemo(() => ({
    algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: isDark ? "#3b9aef" : "#1687e8",
      colorInfo: isDark ? "#3b9aef" : "#1687e8",
      colorSuccess: isDark ? "#3dba9a" : "#16856b",
      colorWarning: isDark ? "#e0a04a" : "#b86d14",
      colorError: isDark ? "#e06b76" : "#c3424f",
      colorText: isDark ? "#e8eef8" : "#172033",
      colorTextHeading: isDark ? "#e8eef8" : "#102a56",
      colorTextSecondary: isDark ? "#9aa8bd" : "#5b6b82",
      colorBorder: isDark ? "#2a3b54" : "#d5e0ec",
      colorBorderSecondary: isDark ? "#1e2c40" : "#e6eef6",
      colorBgBase: isDark ? "#0b1422" : "#f4f7fb",
      colorBgContainer: isDark ? "#121c2e" : "#ffffff",
      colorBgElevated: isDark ? "#182338" : "#ffffff",
      colorBgLayout: isDark ? "#0b1422" : "#f4f7fb",
      colorFillSecondary: isDark ? "#1a2538" : "#eef5fc",
      colorTextDisabled: isDark ? "#667892" : "#98a6bc",
      colorBgMask: isDark ? "rgba(4, 10, 18, 0.72)" : "rgba(16, 42, 86, 0.45)",
      borderRadius: 6,
      fontFamily: "IBM Plex Sans, Noto Sans SC, sans-serif",
      motion: !reducedMotion,
      controlOutline: isDark ? "rgba(59, 154, 239, 0.25)" : "rgba(22, 135, 232, 0.2)",
    },
    components: {
      Button: { controlHeight: compactMode ? 34 : 40 },
      Input: { controlHeight: compactMode ? 36 : 42 },
      Select: { controlHeight: compactMode ? 36 : 42, optionSelectedBg: isDark ? "#1a283c" : "#eef6ff" },
      Form: { itemMarginBottom: compactMode ? 14 : 20 },
      Tabs: {
        horizontalItemPadding: "12px 0",
        itemColor: isDark ? "#9aa8bd" : "#5b6b82",
        itemSelectedColor: isDark ? "#e8eef8" : "#102a56",
        itemHoverColor: isDark ? "#e8eef8" : "#0c72c9",
        inkBarColor: isDark ? "#3b9aef" : "#1687e8",
      },
      Table: {
        headerBg: isDark ? "#152033" : "#eef6ff",
        headerColor: isDark ? "#e8eef8" : "#102a56",
        rowHoverBg: isDark ? "#1a2538" : "#eef5fc",
        rowSelectedBg: isDark ? "#1c3050" : "#e3f1ff",
        rowSelectedHoverBg: isDark ? "#243a5c" : "#d5eaff",
        borderColor: isDark ? "#2a3b54" : "#d5e0ec",
        cellPaddingBlock: compactMode ? 8 : 16,
        cellPaddingInline: compactMode ? 12 : 16,
      },
      Modal: {
        contentBg: isDark ? "#121c2e" : "#ffffff",
        headerBg: isDark ? "#121c2e" : "#ffffff",
        titleColor: isDark ? "#e8eef8" : "#102a56",
        footerBg: isDark ? "#121c2e" : "#ffffff",
      },
      Message: {
        contentBg: isDark ? "#182338" : "#ffffff",
      },
      Notification: {
        colorBgElevated: isDark ? "#182338" : "#ffffff",
      },
      Dropdown: { paddingBlock: 6 },
    },
  }), [isDark, reducedMotion, compactMode]);

  if (setupStatusError) return <div className="boot-screen boot-error"><div><div className="brand-lockup"><span className="brand-mark">OwO!</span><span className="brand-product">Identity</span></div><h1>{translate(locale, "boot.title")}</h1><p>{translate(locale, "boot.hint")}</p><Button type="primary" onClick={() => { setSetupRequired(null); setSetupAttempt((value) => value + 1); }}>{translate(locale, "boot.retry")}</Button></div></div>;
  if (setupRequired === null) return <BootScreen />;

  return <LocaleProvider><ConfigProvider locale={locale === "en" ? enUS : zhCN} theme={antdTheme} getPopupContainer={() => document.body}><QueryClientProvider client={queryClient}><BrowserRouter><Suspense fallback={<BootScreen />}><PageErrorBoundary><Routes><Route path="/setup" element={setupRequired ? <SetupPage /> : <NotFoundPage />} /><Route path="/legal" element={<LegalPage />} /><Route path="/legal/:key" element={<LegalPage />} /><Route path="/oauth/paused" element={<PausedApplicationPage />} /><Route path="/login" element={setupRequired ? <Navigate to="/setup" replace /> : <LoginPage />} /><Route path="/develop/:slug" element={setupRequired ? <Navigate to="/setup" replace /> : <DevelopApplyPage />} /><Route path="/device" element={setupRequired ? <Navigate to="/setup" replace /> : <DevicePage />} /><Route path="/interact" element={setupRequired ? <Navigate to="/setup" replace /> : <InteractPage />} /><Route path="/verify-email" element={setupRequired ? <Navigate to="/setup" replace /> : <VerifyEmailPage />} /><Route path="/reset-password" element={setupRequired ? <Navigate to="/setup" replace /> : <ResetPasswordPage />} /><Route path="/accept-invite" element={setupRequired ? <Navigate to="/setup" replace /> : <AcceptInvitePage />} /><Route element={<AuthGate />}><Route element={<AppShell />}><Route index element={<DashboardPage />} /><Route path="account" element={<AccountPage />} /><Route path="apps" element={<ApplicationsPage />} /><Route path="security" element={<SecurityPage />} /><Route path="settings" element={<SettingsPage />} /><Route path="support" element={<SupportPage />} /><Route path="support/:ticketId" element={<SupportPage />} /><Route path="announcements" element={<AnnouncementsPage />} /><Route path="announcements/:id" element={<AnnouncementsPage />} /><Route path="docs" element={<DocsPage />} /><Route path="docs/:audience/:slug" element={<DocsPage />} /><Route element={<RoleGate anyOf={["oidc_developer", "super_admin", "tenant_withdrawal_initiator"]} />}><Route path="developer" element={<DeveloperPage />} /></Route><Route element={<RoleGate anyOf={["tenant_withdrawal_initiator", "super_admin"]} />}><Route path="withdrawal" element={<WithdrawalPage />} /></Route><Route element={<RoleGate anyOf={["tenant_admin", "super_admin"]} allowPermissions />}><Route path="admin" element={<AdminPage />} /></Route><Route element={<RoleGate anyOf={["super_admin"]} />}><Route path="platform" element={<PlatformPage />} /></Route></Route></Route><Route path="*" element={<NotFoundPage />} /></Routes></PageErrorBoundary></Suspense></BrowserRouter></QueryClientProvider></ConfigProvider></LocaleProvider>;
}
